import React, { FC, useCallback, useEffect } from 'react';
import { NavBar, NavBarProps } from '../../../NavBar';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { OverrideTypography } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.types';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { Category, EventPageFragment, EventPageType } from '@graphql/generated';
import { NavbarEventPropsInterface } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import { WebsiteHeaderColors, WebsiteHeaderTextContent } from '@apps/guest/websiteHeader';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { InlineEditor } from '@shared/components';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { withWindow } from '@shared/utils/withWindow';
import { AnimatedBorder } from '@shared/components/InlineEditor/components/AnimatedBorder/AnimatedBorder';

export interface EventNameNavForStandaloneHeaderProps {
  navBarVariant: NavBarProps['variant'];
  eventHandle: string;
  pages: ReadonlyArray<EventPageFragment>;
  handleMenuClicked?: (item: string) => void;
  eventProps: NavbarEventPropsInterface;
  currentPageType: Maybe<EventPageType>;
  pageSlug?: string;
}

interface Props extends EventNameNavForStandaloneHeaderProps {
  colors?: WebsiteHeaderColors | undefined;
  headerTextData?: WebsiteHeaderTextContent | undefined;
  eventDisplayName: Maybe<string>;
  isCompactView: boolean;
  handleEditTextClick: () => void;
  isContentEditable: boolean;
  editableContentRef?: React.RefObject<HTMLDivElement>;
  forceRenderViewIn?: 'mobile' | 'desktop';
  scaleValue?: number;
}

const typographyOverride: OverrideTypography = [
  { variant: 'display6', category: Category.EVENT_TITLE },
  { variant: 'display4', category: Category.EVENT_TITLE }
];

const computeResponsiveValue = <T,>(defaultValue: T, mobileValue: number, desktopValue: number, forceRenderViewIn?: 'mobile' | 'desktop') => {
  if (forceRenderViewIn === 'mobile') {
    return mobileValue;
  }
  if (forceRenderViewIn === 'desktop') {
    return desktopValue;
  }
  return defaultValue;
};

export const EventNameNavForStandaloneHeader: FC<Props> = props => {
  const {
    handleMenuClicked,
    navBarVariant,
    eventHandle,
    eventProps,
    pages,
    eventDisplayName,
    colors,
    headerTextData,
    isCompactView,
    currentPageType,
    pageSlug,
    handleEditTextClick,
    isContentEditable = false,
    editableContentRef,
    forceRenderViewIn,
    scaleValue = 1
  } = props;

  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isXsScreen = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'xs' }));

  const [fontSize] = useResponsive({ values: { xs: 64, md: 100, lg: 125 } }, 64);

  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const inlineEditorTransFn = useTranslation('joykit').t('inlineEditor');

  useEffect(() => {
    if (editableContentRef?.current && isContentEditable) {
      editableContentRef.current.focus();
      withWindow(globalWindow => {
        // place caret at end of the text
        const range = globalWindow.document.createRange();
        range.selectNodeContents(editableContentRef.current as Node);
        range.collapse(false);
        const sel = globalWindow.getSelection?.();
        sel?.removeAllRanges();
        sel?.addRange(range);
      });
    }
  }, [editableContentRef, isContentEditable]);

  const handleEditFontClick = useEventCallback((category: Category) => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editFont', inlineEditData: { category } }
    });
  });

  const applyScaling = useCallback((value: number) => value / scaleValue, [scaleValue]);

  return (
    <>
      <Box width="100%" pointerEvents={isContentEditable ? 'none' : 'unset'} position="relative" zIndex="docked">
        <NavBar
          handleMenuClicked={handleMenuClicked}
          title={eventDisplayName}
          variant={navBarVariant}
          data-testid={'header-nav'}
          eventHandle={eventHandle}
          pages={pages}
          eventProps={eventProps}
          textColor={colors?.nameAndNavFontColor}
          backgroundColor={colors?.nameAndNavBackgroundColorEnabled ? colors?.nameAndNavBackgroundColor : undefined}
          paddingBottom={isCompactView ? 8 : 0}
        />
      </Box>
      {!isCompactView && (
        <Flex width="100%" position="absolute" zIndex="docked" top="50%" transform="translateY(-50%)" justifyContent={headerTextData?.textAlignment || 'left'}>
          <Box
            marginX={computeResponsiveValue({ _: 24, md: 64, lg: 64 }, 24, 64, forceRenderViewIn)}
            paddingY={7}
            paddingX={6}
            backgroundColor={colors?.nameAndNavBackgroundColorEnabled ? colors?.nameAndNavBackgroundColor : undefined}
          >
            <InlineEditor
              elementLabel={inlineEditorTransFn.couplesName()}
              actionData={{
                edit: handleEditTextClick,
                font: () => handleEditFontClick(Category.EVENT_TITLE)
              }}
              wrapperCSS={{
                zIndex: 1200
              }}
              inset={isMobile ? { x: -20, y: -24 } : { x: -16, y: -20 }}
              componentName="couplesName"
              pageName={currentPageType}
              pageSlug={pageSlug || ''}
            >
              <GuestSiteTypographyOverride override={typographyOverride}>
                <TextV2
                  as="div"
                  typographyVariant={isXsScreen ? 'display4' : 'display6'}
                  color={colors?.nameAndNavFontColor || 'mono14'}
                  fontSize={scaleFontSize(pxToRem(computeResponsiveValue(fontSize, 64, 125, forceRenderViewIn)), Category.EVENT_TITLE)}
                  whiteSpace="pre-wrap"
                  textAlign={headerTextData?.textAlignment || 'left'}
                  position="relative"
                >
                  {isContentEditable ? (
                    <>
                      <AnimatedBorder
                        isVisibile={true}
                        borderWidth={pxToRem(applyScaling(2))}
                        borderRadius={`${applyScaling(8)}px`}
                        inset={isMobile ? { x: '-20px', y: '-24px' } : { x: '-16px', y: '-20px' }}
                        padding={0}
                      />
                      <Box
                        ref={editableContentRef}
                        position="relative"
                        zIndex={11}
                        contentEditable={isContentEditable}
                        dangerouslySetInnerHTML={{ __html: headerTextData?.content || '' }}
                        whiteSpace="pre-wrap"
                        outline="none"
                        style={{
                          caretColor: '#4E06F2'
                        }}
                      />
                    </>
                  ) : (
                    headerTextData?.content || ''
                  )}
                </TextV2>
              </GuestSiteTypographyOverride>
            </InlineEditor>
          </Box>
        </Flex>
      )}
    </>
  );
};

EventNameNavForStandaloneHeader.displayName = 'EventNameNavForStandaloneHeader';
